import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { ToastProvider } from './components/Toast';
import { PERMISSIONS, hasPermission } from './auth/permissions';
import { useTheme } from './styles/ThemeProvider';
import useDisableNumberInputScroll from './hooks/useDisableNumberInputScroll';

// Layout
import Sidebar from './components/Sidebar';
import NotificationBell from './components/NotificationBell';
import ScrollManager from './components/ScrollManager';

// Pages
import PageUsageTracker from './components/PageUsageTracker';
import LoginPage from './pages/LoginPage';
import SifreDegistirZorunlu from './pages/auth/SifreDegistirZorunlu';
import DashboardRouter from './pages/dashboard/DashboardRouter';
import TalepOlusturPage from './pages/talep/TalepOlusturPage';
import TalepListesiPage from './pages/talep/TalepListesiPage';
import SiparislerPage from './pages/siparis/SiparislerPage';
import SiparisOlusturPage from './pages/siparis/SiparisOlusturPage';
import SiparisDetayPage from './pages/siparis/SiparisDetayPage';
import BekleyenOnaylarPage from './pages/onay/BekleyenOnaylarPage';
import GRNDetayPage from './pages/grn/GRNDetayPage';
import BirlesikMalKabulPage from './pages/grn/BirlesikMalKabulPage';
import StokDurumuPage from './pages/stok/StokDurumuPage';
import StokSayimPage from './pages/stok/StokSayimPage';
import StokSayimGecmisPage from './pages/stok/StokSayimGecmisPage';
import StokHareketleriPage from './pages/stok/StokHareketleriPage';
import InventoryPage from './pages/InventoryPage';
import MalKabulPage from './pages/mal-kabul/MalKabulPage';
// ReceteListePage TabContainer (ReceteMaliyetPage) içinde render edilir;
// standalone /recete/listesi route'u kaldırıldı (sekme kaybı bug'ı) → App.js'de import gereksiz.
import ReceteFormPage from './pages/recipe/ReceteFormPage';
import MaliyetAnalizPage from './pages/recipe/MaliyetAnalizPage';
// TransferFiyatPage TabContainer (ReceteMaliyetPage) içinde render edilir;
// standalone /transfer-fiyat route'u redirect'e çevrildi (sekme kaybı bug'ı) → App.js'de import gereksiz.
import FoodCostDashboard from './pages/recipe/FoodCostDashboard';
import FiyatGirisiPage from './pages/finance/FiyatGirisiPage';
import GiderGirisiPage from './pages/finance/GiderGirisiPage';
import GeciciVergiPage from './pages/finance/GeciciVergiPage';
import StopajRaporuPage from './pages/finance/StopajRaporuPage';
import OdemePlaniPage from './pages/finance/OdemePlaniPage';
import CariHesaplarPage from './pages/finance/CariHesaplarPage';
import KasaKapanisPage from './pages/finance/KasaKapanisPage';
import KasaDefterPage from './pages/finance/KasaDefterPage';
import KarZararAnalizPage from './pages/analiz/KarZararAnalizPage';
import SatinalmaAnalizPage from './pages/analiz/SatinalmaAnalizPage';
import MenuKarliligiPage from './pages/analiz/MenuKarliligiPage';
import TedarikciDetayPage from './pages/analiz/TedarikciDetayPage';
import StokUretimAnalizPage from './pages/analiz/StokUretimAnalizPage';
import KomisyonDetayPage from './pages/analiz/KomisyonDetayPage';
import MaliyetVaryansPage from './pages/analiz/MaliyetVaryansPage';
import SatisKanalPage from './pages/analiz/SatisKanalPage';
import UretimKaydiPage from './pages/uretim/UretimKaydiPage';
import POSSatisPage from './pages/pos/POSSatisPage';
import ZayiGirisiPage from './pages/stok/ZayiGirisiPage';
import BirimDonusumPage from './pages/stok/BirimDonusumPage';
import AbcAnalizPage from './pages/stok/AbcAnalizPage';
import ExcelSayimImportPage from './pages/stok/ExcelSayimImportPage';
import TedarikcilerPage from './pages/tedarikci/TedarikcilerPage';
import MusterilerPage from './pages/satislar/MusterilerPage';
import SatisIrsaliyeleriPage from './pages/satislar/SatisIrsaliyeleriPage';
import SatisIrsaliyesiOlusturPage from './pages/satislar/SatisIrsaliyesiOlusturPage';
import SatisFaturalariPage from './pages/satislar/SatisFaturalariPage';
import SatisFaturasiOlusturPage from './pages/satislar/SatisFaturasiOlusturPage';
import TahsilatlarPage from './pages/satislar/TahsilatlarPage';
import AlacakTakibiPage from './pages/satislar/AlacakTakibiPage';
import IadelerPage from './pages/satislar/IadelerPage';
import FiyatTakipPage from './pages/tedarikci/FiyatTakipPage';
import KullanicilarPage from './pages/admin/KullanicilarPage';
import MasterVerilerPage from './pages/admin/MasterVerilerPage';
import VeriDuzeltmePage from './pages/admin/VeriDuzeltmePage';
import SistemAyarlariPage from './pages/SistemAyarlariPage';
import YedeklemePage from './pages/admin/YedeklemePage';
import LogKayitlariPage from './pages/admin/LogKayitlariPage';
import LogoFaturalarPage from './pages/logo/LogoFaturalarPage';
import LogoSenkronizasyonPage from './pages/logo/LogoSenkronizasyonPage';
import EslestirmeMerkeziPage from './pages/entegrasyon/EslestirmeMerkeziPage';
import MenuMuhendisligiPage from './pages/menu-muhendisligi/MenuMuhendisligiPage';
import AnalizSonuclariPage from './pages/menu-muhendisligi/AnalizSonuclariPage';
import FiyatSimulasyonuPage from './pages/menu-muhendisligi/FiyatSimulasyonuPage';
import UrunSatisRaporuPage from './pages/raporlar/UrunSatisRaporuPage';
import SatisAnalizPage from './pages/satis-analiz/SatisAnalizPage';
import SatinalmaPage from './pages/groups/SatinalmaPage';
import StokDepoPage from './pages/groups/StokDepoPage';
import UretimPage from './pages/groups/UretimPage';
import ReceteMaliyetPage from './pages/groups/ReceteMaliyetPage';
import FinansPage from './pages/groups/FinansPage';
import MuhasebeLogoPage from './pages/groups/MuhasebeLogoPage';
import RaporlarGroupPage from './pages/groups/RaporlarGroupPage';
import TedarikciPage from './pages/groups/TedarikciPage';
import EntegrasyonPage from './pages/groups/EntegrasyonPage';
import YonetimPage from './pages/groups/YonetimPage';
import FaturalandirmaPage from './pages/finance/FaturalandirmaPage';
import SirketlerArasiSatislarPage from './pages/finance/SirketlerArasiSatislarPage';
import ZayiRaporuPage from './pages/raporlar/ZayiRaporuPage';
import TuketimRaporuPage from './pages/raporlar/TuketimRaporuPage';
import SktBozulmaPage from './pages/raporlar/SktBozulmaPage';
import IhtiyacListesiPage from './pages/talep/IhtiyacListesiPage';
import TedarikciKarnesiPage from './pages/tedarikci/TedarikciKarnesiPage';
import DogrudenMalKabulPage from './pages/mal-kabul/DogrudenMalKabulPage';
import IkincilIslemePage from './pages/uretim/IkincilIslemePage';
import IkPage from './pages/groups/IkPage';
import ExecutiveCockpit from './pages/yonetim/ExecutiveCockpit';
import PosTerminalleriPage from './pages/ayarlar/PosTerminalleriPage';
import FizibiliteMasterDataPage from './pages/ayarlar/FizibiliteMasterDataPage';
import StokReferansButunluguPage from './pages/ayarlar/StokReferansButunluguPage';
import StokReferansButunluguDetayPage from './pages/ayarlar/StokReferansButunluguDetayPage';


// /transfer-fiyat eski standalone route'u → TabContainer'lı gruba yönlendir (sekme kaybı bug'ı).
// Reçete listesindeki "Satış Fiyatı / Fiyat gir" tıklaması buraya ?arama= ile geliyor; query'yi
// koruyup #transfer-fiyat hash'iyle gruba düşür ki üst sekmeler korunsun + arama filtresi taşınsın.
const TransferFiyatRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/recete-maliyet${search}#transfer-fiyat`} replace />;
};

// ==================== LAYOUT ====================
const AppLayout = () => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const isMobile = window.innerWidth < 768;
  const { theme } = useTheme();
  const defaultRoute = hasPermission(user?.role, PERMISSIONS.VIEW_EXECUTIVE_COCKPIT, user?.effectivePermissions) ? '/yonetim-paneli' : '/dashboard';

  useEffect(() => {
    document.body.style.background = theme.color.surfaceAlt;
    document.body.style.color = theme.color.text;
    document.body.style.fontFamily = theme.font.family;
  }, [theme]);

  return (
    <div style={{ display: 'flex' }}>
      <PageUsageTracker />
      <Sidebar user={user} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} logout={logout} />
      {/* minWidth:0 ŞART — flex child (min-width:auto) geniş içerikte (örn. çok kolonlu
          tablolar) küçülmez → sayfa yatay taşar. minWidth:0 ile içerik kendi
          scroll konteynerinde (overflowX:auto) kayar, sayfa viewport'u aşmaz. */}
      <div style={{ marginLeft: isMobile ? '0' : (sidebarOpen ? '260px' : '64px'), width: '100%', minWidth: 0, transition: 'margin-left 0.3s cubic-bezier(0.4,0,0.2,1)', minHeight: '100vh', background: '#f0f2f5', paddingTop: isMobile ? '50px' : '0' }}>
        <NotificationBell user={user} />
        <Routes>
          <Route path="/dashboard" element={<DashboardRouter />} />

          {/* Grup (Tab) Sayfaları — Sadeleştirilmiş Menü */}
          <Route path="/satinalma" element={<SatinalmaPage />} />
          <Route path="/stok-depo" element={<StokDepoPage />} />
          <Route path="/uretim" element={<UretimPage />} />
          <Route path="/recete-maliyet" element={<ReceteMaliyetPage />} />
          <Route path="/finans" element={<FinansPage />} />
          <Route path="/muhasebe-logo" element={<MuhasebeLogoPage />} />
          <Route path="/raporlar-grup" element={<RaporlarGroupPage />} />
          <Route path="/tedarikciler-grup" element={<TedarikciPage />} />
          <Route path="/entegrasyon" element={<EntegrasyonPage />} />
          <Route path="/yonetim" element={<YonetimPage />} />

          {/* Talep & Sipariş */}
          <Route path="/talep/olustur" element={<TalepOlusturPage />} />
          <Route path="/talep/listesi" element={<TalepListesiPage />} />
          <Route path="/siparisler" element={<SiparislerPage />} />
          <Route path="/siparis/olustur" element={<SiparisOlusturPage />} />
          <Route path="/siparis/detay/:id" element={<SiparisDetayPage />} />
          {/* Mal Kabul (Birleşik) — exact path önce gelsin */}
          <Route path="/dogrudan-mal-kabul" element={<DogrudenMalKabulPage />} />
          <Route path="/mal-kabul" element={<BirlesikMalKabulPage />} />
          <Route path="/mal-kabul/:id" element={<MalKabulPage />} />
          <Route path="/ihtiyac-listesi" element={<IhtiyacListesiPage />} />
          <Route path="/onaylar" element={<BekleyenOnaylarPage />} />
          <Route path="/grn/detay/:id" element={<GRNDetayPage />} />
          {/* Eski GRN route'ları → Birleşik Mal Kabul'e yönlendir */}
          <Route path="/grn/olustur" element={<Navigate to="/mal-kabul" replace />} />
          <Route path="/grn/listesi" element={<Navigate to="/mal-kabul" replace />} />
          <Route path="/kalite-kontrol" element={<Navigate to="/mal-kabul" replace />} />

          {/* Stok & Depo */}
          <Route path="/stok/durum" element={<StokDurumuPage />} />
          <Route path="/stok/sayim" element={<StokSayimPage />} />
          <Route path="/stok/sayim-gecmis" element={<StokSayimGecmisPage />} />
          <Route path="/stok/hareketler" element={<StokHareketleriPage />} />
          <Route path="/zayi-girisi" element={<ZayiGirisiPage />} />
          <Route path="/birim-donusumleri" element={<BirimDonusumPage />} />
          <Route path="/stok/abc-analiz" element={<AbcAnalizPage />} />
          <Route path="/stok/excel-sayim-import" element={<ExcelSayimImportPage />} />
          <Route path="/inventory" element={<InventoryPage />} />

          {/* Üretim — Karkas Parçalama modülü kaldırıldı (paketli ürün alımına geçiş, 2026-05) */}
          <Route path="/uretim-kaydi" element={<UretimKaydiPage />} />
          <Route path="/ikincil-isleme" element={<IkincilIslemePage />} />

          {/* Satış & Analiz (Birleşik) */}
          <Route path="/satis-analiz" element={<SatisAnalizPage />} />
          <Route path="/urun-satis-raporu" element={<UrunSatisRaporuPage />} />

          {/* POS Satış */}
          <Route path="/pos-satış" element={<POSSatisPage />} />
          <Route path="/pos-eşleştirme" element={<Navigate to="/entegrasyon/eslestirme" replace />} />
          {/* Karkas Parçalama modülü kaldırıldı: eski /production, /kesim/*, /blok-test,
              /randiman yer imleri aşağıdaki "*" catch-all ile ana sayfaya düşer. */}

          {/* Reçete & Maliyet */}
          {/* /recete/listesi yetim route: TabContainer'sız standalone liste sekmeleri
              kaybediyordu. Form dönüşleri (ReceteFormPage save/geri) buraya gidiyor;
              canonical grup route'una yönlendir ki üst sekmeler hep korunsun. */}
          <Route path="/recete/listesi" element={<Navigate to="/recete-maliyet" replace />} />
          <Route path="/recete/olustur" element={<ReceteFormPage />} />
          <Route path="/recete/duzenle" element={<ReceteFormPage />} />
          <Route path="/maliyet-analiz" element={<MaliyetAnalizPage />} />
          <Route path="/transfer-fiyat" element={<TransferFiyatRedirect />} />
          <Route path="/food-cost" element={<FoodCostDashboard />} />

          {/* Finans */}
          <Route path="/fiyat-girisi" element={<FiyatGirisiPage />} />
          <Route path="/gider-girisi" element={<GiderGirisiPage />} />
          <Route path="/gecici-vergi" element={<GeciciVergiPage />} />
          <Route path="/stopaj-raporu" element={<StopajRaporuPage />} />
          <Route path="/odeme-planı" element={<OdemePlaniPage />} />
          <Route path="/cari-hesaplar" element={<CariHesaplarPage />} />
          <Route path="/faturalandirma" element={<FaturalandirmaPage />} />
          <Route path="/sirketler-arasi-satislar" element={<SirketlerArasiSatislarPage />} />

          {/* Satışlar (Dış Müşteri) */}
          <Route path="/satislar/musteriler" element={<MusterilerPage />} />
          <Route path="/satislar/irsaliyeler" element={<SatisIrsaliyeleriPage />} />
          <Route path="/satislar/irsaliye/yeni" element={<SatisIrsaliyesiOlusturPage />} />
          <Route path="/satislar/faturalar" element={<SatisFaturalariPage />} />
          <Route path="/satislar/fatura/yeni" element={<SatisFaturasiOlusturPage />} />
          <Route path="/satislar/tahsilatlar" element={<TahsilatlarPage />} />
          <Route path="/satislar/alacak-takibi" element={<AlacakTakibiPage />} />
          <Route path="/satislar/iadeler" element={<IadelerPage />} />

          {/* Raporlar */}
          {/* Analiz sayfaları (Faz 2) */}
          <Route path="/analiz/pnl" element={<KarZararAnalizPage />} />
          <Route path="/analiz/satinalma" element={<SatinalmaAnalizPage />} />
          <Route path="/analiz/satis" element={<MenuKarliligiPage />} />
          <Route path="/analiz/tedarikci/:id" element={<TedarikciDetayPage />} />
          <Route path="/analiz/stok-uretim" element={<StokUretimAnalizPage />} />
          {/* Karar Destek Paneli drill-down sayfaları (2026-05-02) */}
          <Route path="/analiz/komisyon-detay" element={<KomisyonDetayPage />} />
          <Route path="/analiz/maliyet-varyans" element={<MaliyetVaryansPage />} />
          <Route path="/analiz/satis-kanal" element={<SatisKanalPage />} />
          {/* Eski route redirect'leri (bookmark uyumluluğu) */}
          <Route path="/raporlar" element={<Navigate to="/raporlar-grup" replace />} />
          <Route path="/satis-raporu" element={<Navigate to="/analiz/satis" replace />} />
          <Route path="/satinalma-raporu" element={<Navigate to="/analiz/satinalma" replace />} />
          <Route path="/kar-zarar" element={<Navigate to="/analiz/pnl" replace />} />
          <Route path="/zayi-raporu" element={<ZayiRaporuPage />} />
          <Route path="/tuketim-raporu" element={<TuketimRaporuPage />} />
          <Route path="/skt-bozulma" element={<SktBozulmaPage />} />

          {/* Tedarikçi */}
          <Route path="/tedarikciler" element={<TedarikcilerPage />} />
          <Route path="/fiyat-takip" element={<FiyatTakipPage />} />
          <Route path="/tedarikci-karnesi" element={<TedarikciKarnesiPage />} />

          {/* Logo İşbaşı */}
          <Route path="/logo/faturalar" element={<LogoFaturalarPage />} />
          <Route path="/logo/cari-eslestirme" element={<Navigate to="/entegrasyon/eslestirme" replace />} />
          <Route path="/logo/senkronizasyon" element={<LogoSenkronizasyonPage />} />
          <Route path="/entegrasyon/eslestirme" element={<EslestirmeMerkeziPage />} />

          {/* Menü Mühendisliği */}
          <Route path="/menu-muhendisligi" element={<MenuMuhendisligiPage />} />
          <Route path="/analiz-sonuclari" element={<AnalizSonuclariPage />} />
          <Route path="/fiyat-simulasyonu" element={<FiyatSimulasyonuPage />} />

          {/* İnsan Kaynakları — tab container */}
          <Route path="/ik" element={<IkPage />} />

          {/* Admin */}
          <Route path="/kullanicilar" element={<KullanicilarPage />} />
          <Route path="/master-veriler" element={<MasterVerilerPage />} />
          <Route path="/veri-duzeltme" element={<VeriDuzeltmePage />} />
          <Route path="/ayarlar" element={<SistemAyarlariPage />} />
          <Route path="/ayarlar/pos-terminalleri" element={<PosTerminalleriPage />} />
          <Route path="/ayarlar/fizibilite-tanimlar" element={<FizibiliteMasterDataPage />} />
          <Route path="/ayarlar/stok-referans-butunlugu" element={<StokReferansButunluguPage />} />
          <Route path="/ayarlar/stok-referans-butunlugu/detay" element={<StokReferansButunluguDetayPage />} />
          <Route path="/yedekleme" element={<YedeklemePage />} />
          <Route path="/log-kayitlari" element={<LogKayitlariPage />} />

          {/* Yönetim Paneli (Executive Cockpit) — Faz 1 */}
          <Route path="/yonetim-paneli" element={<ExecutiveCockpit />} />
          {/* Kasa Doğrulama menüden kaldırıldı → Kasa Defteri'ne yönlendir
              (Cockpit "kasa" KPI drill-down ve eski yer imleri kırılmasın). */}
          <Route path="/kasa-dogrulama" element={<Navigate to="/kasa-defter" replace />} />
          <Route path="/kasa-kapanis" element={<KasaKapanisPage />} />
          <Route path="/kasa-defter" element={<KasaDefterPage />} />

          {/* Fallback */}
          <Route path="/" element={<Navigate to={defaultRoute} replace />} />
          <Route path="*" element={<Navigate to={defaultRoute} replace />} />
        </Routes>
      </div>
    </div>
  );
};

// ==================== GLOBAL RESPONSIVE STYLES ====================
const GlobalResponsiveStyles = () => (
  <style>{`
    /* Table horizontal scroll wrapper */
    @media (max-width: 768px) {
      table {
        display: block;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
      }
      thead, tbody, tr {
        display: table;
        width: 100%;
        table-layout: auto;
      }
      thead { display: table-header-group; }
      tbody { display: table-row-group; }
    }

    /* Touch targets */
    @media (max-width: 768px) {
      button, [role="button"] {
        min-height: 44px;
      }
    }

    /* Global typography */
    *, *::before, *::after { box-sizing: border-box; }
    body {
      margin: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      color: #1a2332;
      overflow-x: hidden;
      line-height: 1.5;
    }
    button, input, select, textarea {
      font-family: inherit;
      font-size: inherit;
    }
    table { font-size: 13px; }
    h1, h2, h3, h4 { letter-spacing: -0.01em; }

    /* Tab scrollbar hide */
    .tab-scroll-container::-webkit-scrollbar { display: none; }
    .tab-scroll-container { scrollbar-width: none; -ms-overflow-style: none; }
  `}</style>
);

// ==================== APP ====================
const App = () => (
  <BrowserRouter>
    <GlobalResponsiveStyles />
    <ScrollManager />
    <AuthProvider>
      <ToastProvider>
        <AppRoot />
      </ToastProvider>
    </AuthProvider>
  </BrowserRouter>
);

const AppRoot = () => {
  const { user, loading, mustChangePassword } = useAuth();

  // Global: mouse tekerleğiyle number input değerlerinin kazara değişmesini engelle.
  useDisableNumberInputScroll();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '18px', color: '#666' }}>
        Yükleniyor...
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  if (mustChangePassword) {
    return <SifreDegistirZorunlu />;
  }

  return <AppLayout />;
};

export default App;
